import * as React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import { Link, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Paper, Backdrop } from '@mui/material';
import { grey, red } from '@mui/material/colors';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useAuth } from '../../../components/AuthAdminProvider';
import theme from './themeLogin';
import backgroundImg from '../../../components/imgs/background.jpg';
import CustomTextField from '../../../components/CustomTextField';

export default function LoginAdminPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const location = useLocation();

  const [loading, setLoading] = React.useState(false);
  const [loadingMessage, setLoadingMessage] = React.useState('');

  React.useEffect(() => {
    document.title = "IPV7 Anubis - Login Administrativo";
  }, []);

  const [errorInput, setErrorInput] = React.useState({
    username: false,
    password: false,
  });

  const [errorMsg, setErrormsg] = React.useState({
    severity: "error",
    msg: "",
    show: false
  });

  const loginApi = async (cred) => {
    let from = location.state?.from?.pathname || "/admin";

    setLoading(true);
    setLoadingMessage('Verificando credenciais...');

    const redirectTo = () => {
      setLoading(false);
      navigate(from, { replace: true });
    };

    const cbErr = (errorMessage) => {
      setLoading(false);
      setLoadingMessage('');
      setErrormsg({
        msg: errorMessage || "Credenciais invalidas.",
        show: true,
        severity: 'error'
      });
    };

    const on2FA = () => {
      setLoadingMessage('Enviando codigo de verificacao...');
    };

    auth.signin(cred, redirectTo, cbErr, on2FA);
  };

  const handleSubmit = (event) => {
    setErrorInput(obj => ({ ...obj, username: false, password: false }));
    setErrormsg(errorMsg => ({ ...errorMsg, show: false }));
    event.preventDefault();

    const data = new FormData(event.currentTarget);
    const cred = ({
      username: data.get('username'),
      password: data.get('password'),
    });

    if (cred.password.length < 8) {
      setErrorInput(obj => ({ ...obj, password: true }));
      return;
    }
    if (cred.username.length < 1) {
      setErrorInput(obj => ({ ...obj, username: true }));
      return;
    }
    if (!cred.username || !cred.password || cred.password.length < 5 || cred.username.length < 1) {
      setErrormsg(m => ({ show: true, msg: "Por favor preencha o formulario corretamente.", severity: 'warning' }));
      return;
    }

    loginApi(cred);
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Backdrop de Loading */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={loading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {loadingMessage}
        </Typography>
      </Backdrop>

      <div
        component="main"
        style={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'end',
          justifyContent: 'center',
          height: '100vh',
          backgroundImage: `url(${backgroundImg})`,
          backgroundPosition: 'left',
          backgroundSize: 'cover',
        }}
      >
        <CssBaseline />
        <Box minWidth={300} sx={{ display: 'flex' }}>
          <Box
            component={"div"}
            sx={{
              height: '100vh',
              m: 0,
              backgroundColor: "#C4161C",
              opacity: 0.99,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Paper
              elevation={2}
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                mt: 1,
                maxHeight: 450,
                width: 500,
                p: 5,
                m: 5,
                backgroundColor: 'white',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    backgroundColor: grey[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 28, color: grey[700] }} />
                </Box>
                <Typography component="h1" variant="h5" align="center">
                  Login Administrativo
                </Typography>
              </Box>

              <CustomTextField
                error={errorInput.username}
                margin="normal"
                required
                fullWidth
                id="username"
                label="Usuario"
                name="username"
                autoComplete="username"
                autoFocus
                disabled={loading}
                helperText={errorInput.username ? "Verifique este campo" : ''}
              />

              <CustomTextField
                error={errorInput.password}
                margin="normal"
                required
                fullWidth
                name="password"
                minLength="8"
                label="Senha"
                type="password"
                id="password"
                autoComplete="current-password"
                disabled={loading}
                helperText={errorInput.password ? "Verifique este campo. A senha deve ter no minimo 8 caracteres" : ''}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  backgroundColor: grey[900],
                  '&:hover': { backgroundColor: grey[800] },
                  height: 45
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Acessar'
                )}
              </Button>

              <Link to={"/recovery?source=admin"} style={{ textDecoration: 'none', color: red[900] }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  Esqueci minha senha
                </Typography>
              </Link>

              {errorMsg.show && (
                <Alert severity={errorMsg.severity} sx={{ mt: 2 }}>
                  {errorMsg.msg}
                </Alert>
              )}
            </Paper>
          </Box>
        </Box>
      </div>
    </ThemeProvider>
  );
}
